import React from 'react';
import { Flex, Loader, Square } from '@grubhubprod/gh-cookbook-react';
import { useHistory } from 'react-router-dom';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { IErrorResponse } from 'src/utilities/shared-types';
import {
  SdkErrorResponse,
  pushErrorFromAPI,
  pushSuccess,
} from 'src/utilities/notifications';
import PageHeader from 'src/components/enterprise/layout/PageHeader';
import { PageTitle } from 'src/components/enterprise/layout/PageTitle/PageTitle';
import { Breadcrumbs } from 'src/components/Breadcrumbs/Breadcrumbs';
import { useEmployeeEditPage } from 'src/components/enterprise/features/Employee/EditPage/hooks/useEmployeeEditPage';
import { EmployeeFormWrapper } from 'src/components/enterprise/features/Employee/components/EmployeeFormWrapper/EmployeeFormWrapper';
import { EmployeeFormProvider } from 'src/components/enterprise/features/Employee/components/Form/EmployeeFormProvider/EmployeeFormProvider';
import { Employee } from 'src/types/Employee';
import {
  buildCAUIPath,
  buildCMUIPath,
  CAUIPage,
  CMUIPage,
  getHomeBreadcrumb,
} from 'src/constants/urls';
import { useEmployeeListFeatureAccess } from 'src/components/enterprise/features/Employee/Routes/useEmployeeListFeatureAccess/useEmployeeListFeatureAccess';

export const EmployeeEditPage: React.FC = () => {
  const { corpId } = useAuth();
  const { canAccessEmployeeList } = useEmployeeListFeatureAccess();
  const { push } = useHistory();
  const onSuccess = async (employee: Employee): Promise<void> => {
    pushSuccess(`${employee.firstName}'s info has been updated`);
    push(
      buildCMUIPath(CMUIPage.EMPLOYEE_DETAILS, {
        corpId,
        employeeId: employee.id,
      })
    );
  };
  const onError = async (
    error: IErrorResponse | SdkErrorResponse
  ): Promise<void> => {
    pushErrorFromAPI(error, {
      fallbackMessage: 'Employee info was not updated. Try again.',
    });
  };

  const {
    getEmployeeLoading,
    hasGetEmployeeError,
    updateEmployee,
    employeeFormValues,
    employeeData,
  } = useEmployeeEditPage({
    onSuccess,
    onError,
  });

  if (hasGetEmployeeError) {
    return null;
  }

  return (
    <Square inset="lg">
      <PageHeader>
        <PageTitle title="Edit Employee" />
        <Breadcrumbs
          breadcrumbs={[
            {
              label: 'Admin home',
              ...getHomeBreadcrumb(corpId),
            },
            {
              label: 'Employees',
              href: canAccessEmployeeList
                ? buildCMUIPath(CMUIPage.EMPLOYEES, { corpId })
                : buildCAUIPath(CAUIPage.EMPLOYEES, { corpId }),
              useRouterLink: canAccessEmployeeList,
            },
          ]}
        />
      </PageHeader>
      {getEmployeeLoading ? (
        <Flex justifyContent="center">
          <Loader size="lg" />
        </Flex>
      ) : (
        <EmployeeFormProvider employeeData={employeeData}>
          <EmployeeFormWrapper
            defaultValues={employeeFormValues}
            onSubmit={updateEmployee}
          />
        </EmployeeFormProvider>
      )}
    </Square>
  );
};
